/**
 * This function calculates a password strength level from 0 to 4 based on the given password.
 * @param password The password to calculate the strength level for.
 * @returns The password strength level from 0 to 4.
 */
export function calculatePasswordStrength(password: string | undefined): number {
	const normalizedPassword = password ?? "";

	const rules = [
		{ key: "lowercase", regex: /[a-z]/ },
		{ key: "uppercase", regex: /[A-Z]/ },
		{ key: "number", regex: /[0-9]/ },
		{ key: "symbol", regex: /[^a-zA-Z0-9]/ },
	];

	const diversity = rules.reduce(
		(count, rule) => (rule.regex.test(normalizedPassword) ? count + 1 : count),
		0,
	);
	const length = normalizedPassword.length;

	const levels = [
		{ id: 0, minDiversity: 0, minLength: 0 },
		{ id: 1, minDiversity: 2, minLength: 8 },
		{ id: 2, minDiversity: 3, minLength: 10 },
		{ id: 3, minDiversity: 4, minLength: 12 },
		{ id: 4, minDiversity: 4, minLength: 16 },
	];

	for (const level of levels.slice().reverse()) {
		if (diversity >= level.minDiversity && length >= level.minLength) {
			return level.id;
		}
	}

	return 0;
}
/**
const defaultOptions = [
  {
    id: 0,
    value: "Too weak",
    minDiversity: 0,
    minLength: 0,
  },
  {
    id: 1,
    value: "Weak",
    minDiversity: 2,
    minLength: 8,
  },
  {
    id: 2,
    value: "Medium",
    minDiversity: 4,
    minLength: 10,
  },
  {
    id: 3,
    value: "Strong",
    minDiversity: 4,
    minLength: 12,
  },
];

const owaspSymbols = "!\"#$%&'()*+,-./\\:;<=>?@[]^_`{|}~";

const passwordStrength = (
  password,
  options = defaultOptions,
  restrictSymbolsTo
) => {
  options[0].minDiversity = 0;
  options[0].minLength = 0;

  // prevent [a-z] to match null and compute length
  const _password = password ?? "";

  const rules = [
    {
      key: "lowercase",
      regex: "[a-z]",
    },
    {
      key: "uppercase",
      regex: "[A-Z]",
    },
    {
      key: "number",
      regex: "[0-9]",
    },
    {
      key: "symbol",
      regex: restrictSymbolsTo ? `[${escapeStringRegexp(restrictSymbolsTo)}]` : "[^a-zA-Z0-9]",
    },
  ];

  let strength = {};

  strength.contains = rules
    .filter((rule) => new RegExp(`${rule.regex}`).test(_password))
    .map((rule) => rule.key);

  strength.length = _password.length;

  let fulfilledOptions = options
    .filter((option) => strength.contains.length >= option.minDiversity)
    .filter((option) => strength.length >= option.minLength)
    .sort((o1, o2) => o2.id - o1.id)
    .map((option) => ({ id: option.id, value: option.value }));

  Object.assign(strength, fulfilledOptions[0]);

  return strength;
};

export default { passwordStrength, defaultOptions, owaspSymbols }
export { passwordStrength, defaultOptions, owaspSymbols }
 */
